import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, Info } from "lucide-react";
import { ComboExplorer, useScienceSettings } from "../site/sections/ScienceSections";
import { PageLoader } from "../components/LoadingRing";
import { usePageSeo } from "../lib/usePageSeo";
import { crumbsJsonLd } from "../lib/seoHead";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SYSTEM_LABEL = { unani: "Unani", ayurveda: "Ayurveda", nutrition: "Nutrition science", combination: "Unani + Nutrition" };
const EVIDENCE_LABEL = {
    traditional_use: "Traditional use", preclinical: "Early research",
    human_evidence: "Human research", established_nutritional_role: "Established nutritional role",
    limited_evidence: "Limited evidence", under_review: "Under review",
};

const Block = ({ title, body }) => (body ? (
    <div className="border-t border-white/8 pt-6 mt-6">
        <div className="mono text-[10px] tracking-[0.2em] text-gold">{title.toUpperCase()}</div>
        <p className="text-zinc-300 mt-3 leading-relaxed whitespace-pre-line">{body}</p>
    </div>
) : null);

const IngredientRow = ({ row, info }) => {
    const [open, setOpen] = useState(false);
    const detail = [info?.nutrition, info?.unani, info?.ayurveda, info?.role, row.note].filter(Boolean);
    return (
        <div className="border-b border-white/8" data-testid={`ingredient-row-${row.id || row.name}`}>
            <button onClick={() => detail.length && setOpen(!open)} className="w-full text-left py-3.5 flex items-baseline gap-3">
                <span className="flex-1 text-sm">
                    {row.name}
                    {!!(row.combo_names || []).length && (
                        <span className="ml-2 mono text-[8px] tracking-[0.14em] text-lime border border-lime/30 px-1.5 py-0.5">
                            {row.combo_names[0].replace(" Combo", "").toUpperCase()}
                        </span>
                    )}
                    {row.botanical_name && <em className="block text-xs text-zinc-500 mt-0.5">{row.botanical_name}</em>}
                </span>
                <span className="mono text-[10px] text-zinc-500 shrink-0">{row.dose} {row.unit}</span>
                {!!detail.length && <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />}
            </button>
            {open && (
                <div className="pb-4 space-y-2" data-testid={`ingredient-detail-${row.name}`}>
                    {info?.evidence_level && (
                        <span className="mono text-[9px] tracking-[0.14em] text-zinc-500 border border-white/10 px-2 py-0.5">
                            {(EVIDENCE_LABEL[info.evidence_level] || info.evidence_level).toUpperCase()}
                        </span>
                    )}
                    {detail.map((d, i) => <p key={i} className="text-sm text-zinc-400 leading-relaxed">{d}</p>)}
                </div>
            )}
        </div>
    );
};

export default function ScienceProduct() {
    const { productSlug } = useParams();
    const { data: settings } = useScienceSettings();
    const { data, isLoading, isError } = useQuery({
        queryKey: ["science-product", productSlug],
        queryFn: async () => {
            const r = await fetch(`${BASE}/api/science/products/${productSlug}`);
            if (!r.ok) throw new Error("not found");
            return r.json();
        },
    });

    const sciProduct = data?.product || {};
    usePageSeo(data ? {
        title: data.seo?.seo_title || `${sciProduct.name || "Formulation"} — the science | ReHealthMe`,
        description: data.seo?.meta_description || data.overview
            || `How ${sciProduct.name || "this formulation"} is put together: actives, doses, traditional use and what the research looks at.`,
        canonical: `${window.location.origin}/science/${productSlug}`,
        image: (sciProduct.images || [])[0] || sciProduct.image || "",
        jsonLd: [crumbsJsonLd([
            { name: "Home", path: "/" }, { name: "The Science", path: "/science" },
            { name: sciProduct.name || productSlug, path: `/science/${productSlug}` },
        ])],
    } : null);

    if (isLoading) return <PageLoader />;
    if (isError || !data) {
        return (
            <div className="pt-40 pb-40 text-center" data-testid="science-product-not-found">
                <h1 className="font-display text-3xl uppercase tracking-tight">No science page yet</h1>
                <Link to="/science" className="mono text-[10px] tracking-[0.18em] text-gold mt-5 inline-block">BACK TO THE SCIENCE</Link>
            </div>
        );
    }

    const product = data.product || {};
    const combos = (data.formulation?.combos || []).filter((c) => c.is_active !== false);
    const rows = data.ingredients || [];

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid={`science-product-${productSlug}`}>
            <div className="mx-auto max-w-5xl px-6 md:px-12">
                <Link to="/science" data-testid="science-back"
                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.18em] text-zinc-500 hover:text-gold">
                    <ChevronLeft className="w-3.5 h-3.5" /> THE SCIENCE
                </Link>

                <div className="mt-6 grid md:grid-cols-[1fr_220px] gap-8 items-start">
                    <div>
                        <div className="eyebrow mb-3">Reveal the science</div>
                        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight">{product.name}</h1>
                        <div className="mono text-[10px] tracking-[0.18em] text-zinc-500 mt-3">
                            {[product.product_type, data.category].filter(Boolean).join(" · ").toUpperCase()}
                        </div>
                        <p className="mt-4 text-zinc-400">{data.positioning}</p>
                        <div className="mt-5 flex flex-wrap gap-2">
                            <span className="mono text-[9px] tracking-[0.14em] text-gold border border-gold/30 px-2 py-1">
                                {(SYSTEM_LABEL[data.traditional_system] || "").toUpperCase()}
                            </span>
                            {product.status !== "active" && (
                                <span className="mono text-[9px] tracking-[0.14em] text-gold border border-gold/40 px-2 py-1">COMING SOON</span>
                            )}
                            {product.status === "active" && product.purchasable && (
                                <Link to={`/product/${product.slug}`} data-testid="science-buy-link"
                                    className="mono text-[9px] tracking-[0.14em] text-lime border border-lime/40 px-2 py-1">
                                    VIEW PRODUCT →
                                </Link>
                            )}
                        </div>
                    </div>
                    {(data.science_image || product.images?.[0]) && (
                        <img src={data.science_image || product.images[0]} alt={product.name}
                            className="w-full object-contain border border-white/10 bg-bg1 p-4" />
                    )}
                </div>

                {!!combos.length && (
                    <section className="mt-14" data-testid="science-combos">
                        <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-6">
                            {data.formulation?.sections?.combo_title || "Formulation combos"}
                        </h2>
                        <ComboExplorer combos={combos} ingredients={rows}
                            basisLabel={data.formulation?.basis_label || "per tablet"} />
                    </section>
                )}

                <section className="mt-14">
                    <Block title="Overview" body={data.overview} />
                    <Block title="Formulation philosophy" body={data.philosophy} />
                    <Block title="Unani perspective" body={data.unani} />
                    <Block title="Ayurvedic perspective" body={data.ayurveda} />
                    <Block title="Nutrition science" body={data.nutrition} />
                    <Block title="Why these ingredients" body={data.why_selected} />
                    <Block title="How they work together" body={data.synergy} />
                    <Block title="Recommended use" body={data.usage} />
                    <Block title="Cautions" body={data.cautions} />
                </section>

                <section className="mt-14" data-testid="science-full-formulation">
                    <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">Full formulation</h2>
                    <p className="text-sm text-zinc-500 mt-2">
                        {rows.length} ingredients{data.formulation?.basis_label ? ` · ${data.formulation.basis_label}` : ""}.
                        Tap an ingredient for its traditional and nutritional notes.
                    </p>
                    <div className="mt-6 border-t border-white/8">
                        {rows.map((row) => (
                            <IngredientRow key={row.id || row.name} row={row} info={data.library?.[row.name]} />
                        ))}
                    </div>
                </section>

                <section className="mt-14 border border-gold/30 bg-gold/[0.03] p-8" data-testid="science-product-cta">
                    <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">
                        Have questions about what may suit you?
                    </h2>
                    <p className="text-zinc-400 mt-3">Get personalised guidance from our consultation team.</p>
                    <Link to="/consultation" className="btn-pill btn-primary mt-6 inline-flex">BOOK A CONSULTATION</Link>
                </section>

                {(data.disclaimer || settings?.disclaimer) && (
                    <div className="mt-10 border border-white/10 bg-white/[0.02] p-6 flex items-start gap-3">
                        <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <p className="text-xs text-zinc-500 leading-relaxed">{data.disclaimer || settings.disclaimer}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
